import React, { useState } from 'react';
import { Link, Outlet } from 'react-router-dom';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';

const Layout: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  const [isBlinking, setIsBlinking] = useState(false);

  const handleThemeToggle = () => {
    if (isBlinking) return; // Prevent multiple clicks while animating
    setIsBlinking(true);
    toggleDarkMode(); // Toggle immediately
    setTimeout(() => {
      setIsBlinking(false);
    }, 300); // Complete the blink animation
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'bg-black' : 'bg-stone-50'}`}>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isDarkMode ? 'bg-black/80' : 'bg-stone-50/80'} backdrop-blur-sm border-b ${isDarkMode ? 'border-gray-800' : 'border-stone-200'}`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center py-4 sm:py-5 md:py-6">
            {/* Eye Icon at the top */}
            <button 
              onClick={handleThemeToggle}
              className={`transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-stone-900'} focus:outline-none p-1 mb-4 sm:mb-5 md:mb-6`}
              aria-label="Toggle dark mode"
            >
              {isBlinking ? (
                <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" className="sm:w-8 sm:h-8 md:w-8 md:h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="1" y1="12" x2="23" y2="12" stroke="currentColor" strokeWidth="3"/>
                </svg>
              ) : (
                <svg className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10" viewBox="0 0 24 24" fill="none">
                  <ellipse cx="12" cy="12" rx="9" ry="6" fill="currentColor" className={isDarkMode ? 'text-white' : 'text-stone-900'}/>
                  <circle cx="12" cy="12" r="3" fill="currentColor" className={isDarkMode ? 'text-black' : 'text-stone-50'}/>
                </svg>
              )}
            </button>
            
            {/* Navigation row with JAI, links, and icons */}
            <div className="flex justify-between items-center w-full">
              <Link to="/" className={`font-serif text-2xl sm:text-2xl md:text-3xl transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
                JAI
              </Link>
              
              <div className="flex gap-4 sm:gap-8 md:gap-16">
                <Link to="/projects" className={`font-serif text-base sm:text-lg md:text-xl transition-colors duration-300 ${isDarkMode ? 'text-white hover:text-red-500' : 'text-stone-800 hover:text-stone-900'} focus:outline-none`}>
                  Projects
                </Link>

                <Link to="/blog" className={`font-serif text-base sm:text-lg md:text-xl transition-colors duration-300 ${isDarkMode ? 'text-white hover:text-red-500' : 'text-stone-800 hover:text-stone-900'} focus:outline-none`}>
                  Blog
                </Link>
              </div>
              
              <div className="flex gap-3 sm:gap-4 md:gap-6 items-center">
                <a href="https://github.com/JaiJC" target="_blank" rel="noopener noreferrer" className={`transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
                  <FaGithub size={20} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </a>
                <a href="https://www.linkedin.com/in/jai-choraria/" target="_blank" rel="noopener noreferrer" className={`transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
                  <FaLinkedin size={20} className="sm:w-5 sm:h-5 md:w-6 md:h-6" />
                </a>
                <a href="mailto:jchoraria17@gmail.com" rel="noopener noreferrer" className={`transition-colors duration-300 ${isDarkMode ? 'text-white' : 'text-stone-900'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" className="sm:w-5 sm:h-5 md:w-6 md:h-6" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </nav>
      <main className="pt-32">
        <Outlet />
      </main>
      <footer className={`transition-colors duration-300 ${isDarkMode ? 'bg-black border-gray-800' : 'bg-stone-50 border-stone-200'} border-t mt-12 sm:mt-16 md:mt-20`}>
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-7 md:py-8">
          <div className={`text-center text-xs sm:text-sm font-serif transition-colors duration-300 ${isDarkMode ? 'text-gray-400' : 'text-stone-500'}`}>
            © {new Date().getFullYear()} Jai. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;